import { useEffect, useRef, useState } from "react";
import { Message } from "../components/interfaces/message.interface";
import Axios, { CancelTokenSource } from 'axios';
import { ProductType } from "./common/common-enums";
import { getDarkMode, getResponseType, getSessionId, initNewSessionId, setDarkMode } from "./common/storage";
import { v4 as uuidv4 } from 'uuid';
import { isArabicText } from "./common/helpers";

export const useChatBot = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState<string>("");
    const [isTyping, setIsTyping] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const [themeMode, setThemeMode] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const cancelTokenRef = useRef<CancelTokenSource | null>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages,]);

    const cancelOngoingRequest = () => {
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel('Request cancelled by user');
            cancelTokenRef.current = null;
        }
    };

    const toggleDarkMode = () => {
            setDarkMode(!themeMode);
            setThemeMode(!themeMode);
        };

    const handleSettingsShow = () => setShowSettings(true);

    const handleSettingsClose = () => setShowSettings(false);

    const appendUserMessage = (userMessage: Message) => {
        setMessages(prevMessages => [...prevMessages, userMessage]);
        setInput("");
        setIsTyping(true);
    }

    const clearMessages = () => {
        cancelOngoingRequest();
        setIsTyping(false);
        setMessages(() => []);
        initNewSessionId();
    };

    const sendMessage = async (): Promise<void> => {
        if (input.trim() !== "") {

            const { REACT_APP_LLM_RESPONSE }: any = process.env;
            const userMessage: Message = { sender: "user", text: input, id: uuidv4() };
            const language = isArabicText(input) ? "ar" : "en";
            const responseType = getResponseType();
            appendUserMessage(userMessage);
            try {
                cancelTokenRef.current = Axios.CancelToken.source();
                const response = await Axios.post(REACT_APP_LLM_RESPONSE, {
                    transcription: input, chatHistory: [], sessionId: getSessionId(), productType: ProductType.CHATBOT, requestType: "chat", responseType, language
                },
                    {
                        cancelToken: cancelTokenRef.current.token
                    });
                const { answer, conversationId } = response.data.data;
                setMessages(prevMessages => [...prevMessages, { sender: "bot", text: answer, id: uuidv4(), conversationId }]);
            } catch (error) {
                if (Axios.isCancel(error)) {
                    console.log('Request cancelled:', error.message);
                } else {
                    console.error('Error during message send:', error);
                }
            } finally {
                setIsTyping(false);
                cancelTokenRef.current = null;
            }
        }
    };

     const regenerateAnswer = async (input: string): Promise<void> => {
            const { REACT_APP_LLM_RESPONSE }: any = process.env;
            const language = isArabicText(input) ? "ar" : "en";
            try {
                const responseType = getResponseType();
                cancelTokenRef.current = Axios.CancelToken.source();
                const response = await Axios.post(REACT_APP_LLM_RESPONSE, {
                    transcription: input, chatHistory: [], sessionId: getSessionId(), productType: ProductType.CHATBOT, regenerate: true, requestType: "chat",responseType,language,
                }, {
                    cancelToken: cancelTokenRef.current.token
                }
                );
                const resp = response?.data.data.answer;
                if(resp){
                 setMessages(prevMessages => [...prevMessages, { sender: "bot", text: resp, id: uuidv4() }]);
                }else{
                     console.log('No Reponse or the request');
                }
            } catch (error) {
                if (Axios.isCancel(error)) {
                    console.log('Request cancelled:', error.message);
                } else {
                    console.error('Error during message send:', error);
                }
            } finally {
                setIsTyping(false);
                cancelTokenRef.current = null;
            }
        };

    const copyToClipboard = async(text: string) => {
        console.log(text)
        await navigator.clipboard.writeText(text);
    };

    const sendFeedback = async (conversationId: string, rate: number) => {
        const { REACT_APP_RATE_ANSWER }: any = process.env;
        const url = REACT_APP_RATE_ANSWER.replace("{conversationId}", conversationId);
        await Axios.put(url, { rate });
    }

    const updateTyping = (bool: boolean) => setIsTyping(bool);

    const deleteMessage = (id: string) => {
        setMessages(prevMessages => prevMessages.filter(message => message.id != id));
    }

    return { messages, deleteMessage, clearMessages, sendFeedback, updateTyping, input, regenerateAnswer, setInput, sendMessage, isTyping, toggleDarkMode, copyToClipboard, themeMode, setDarkMode, showSettings, handleSettingsShow, handleSettingsClose, messagesEndRef };
};
